import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Plus, Trash2, Calendar, Hash, FileText, User, Building, ArrowLeft, Eye, Edit, Calculator, Check, Search, X, Upload, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { salesOrderApiService, SalesOrder, SalesOrderDetailCreateRequest } from "../services/salesOrderApi";
import { customerApiService, Customer } from "../services/customerApi";
import { Endgood, endgoodApiService, EndgoodCategory, endgoodCategoryApiService, EndgoodIdNameCategoryDto } from '../services';
import { formatDateForForm, formatDateForAPI, getCurrentDate, getFutureDate } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";

type RouteMode = 'create' | 'edit' | 'view';

interface LocationState {
  mode: RouteMode;
  salesOrder?: SalesOrder;
}

interface LineItem {
  id: string;
  endgoodId: string; // Reference to the endgood from the API
  endgoodCategoryId: string; // Reference to the endgood category from the API
  partCategory: string;
  partName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  taxRate: number;
  total: number;
  hsn?: string;
  unit?: string;
  igst?: number;
  cgst?: number;
  sgst?: number;
  totalTax?: number;
  amount?: number;
}

interface SalesOrderData {
  orderNumber: string;
  orderDate: string;
  status: string;
  customerId: number;
  branchId: number;
  effectiveStartDate: string;
  effectiveEndDate: string;
  refNo: string;
  refDate: string;
  notes: string;
  customerAddress: string;
  totalAmount: number;
  taxAmount: number;
  grandTotal: number;
  isActive: boolean;
  lineItems: LineItem[];
}

export function SalesOrderForm() {
  // Store raw input values for rate and quantity to allow decimal typing
  const [rawInputs, setRawInputs] = useState<Record<string, { rate?: string; qty?: string }>>({});
  
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { mode, salesOrder } = (location.state as LocationState) || { mode: 'create' as RouteMode };
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  
  // Determine the actual mode based on route params and state
  const getActualMode = (): RouteMode => {
    if (params.id) {
      if (window.location.pathname.includes('/edit')) return 'edit';
      if (window.location.pathname.includes('/view')) return 'view';
      return 'edit'; // Default to edit if no specific mode
    }
    return 'create';
  };
  
  const actualMode = getActualMode();
  const isViewMode = actualMode === 'view';
  const isEditMode = actualMode === 'edit';
  /** Customer/status/notes stay read-only after create; dates & refs are editable on edit. */
  const isHeaderReadOnly = isViewMode || isEditMode;
  const isDateRefReadOnly = isViewMode;
  
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [items, setItems] = useState<Endgood[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [categories, setCategories] = useState<EndgoodCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [endgoodsByCategory, setEndgoodsByCategory] = useState<Record<string, Endgood[]>>({});
  const [loadingEndgoodsByCategory, setLoadingEndgoodsByCategory] = useState<Record<string, boolean>>({});
  const [savingQuantity, setSavingQuantity] = useState<Record<string, boolean>>({});
  const [savingNewItem, setSavingNewItem] = useState<Record<string, boolean>>({});
  
  // Dialog state for endgood selection
  const [endgoodDialogOpen, setEndgoodDialogOpen] = useState(false);
  const [allEndgoods, setAllEndgoods] = useState<Endgood[]>([]);
  const [loadingAllEndgoods, setLoadingAllEndgoods] = useState(false);
  const [selectedEndgoodIds, setSelectedEndgoodIds] = useState<Set<number>>(new Set());
  const [endgoodSearchTerm, setEndgoodSearchTerm] = useState('');

  // CSV import: parsed rows and preview dialog
  const [csvImportDialogOpen, setCsvImportDialogOpen] = useState(false);
  const [csvParsedRows, setCsvParsedRows] = useState<{ name: string; quantity: number }[]>([]);
  const csvFileInputRef = React.useRef<HTMLInputElement>(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closingOrder, setClosingOrder] = useState(false);

  useEffect(() => {
    if (csvImportDialogOpen && csvParsedRows.length > 0) {
      loadAllEndgoods();
    }
  }, [csvImportDialogOpen]);
  
  const [formData, setFormData] = useState<SalesOrderData>({
    orderNumber: '', // Will be generated by server for new orders
    orderDate: getCurrentDate(), // Format as yyyy-MM-dd
    status: 'OPEN', // Always OPEN for new orders
    customerId: 0, // No customer selected by default
    branchId: selectedBranch || 0,
    effectiveStartDate: getCurrentDate(), // Format as yyyy-MM-dd
    effectiveEndDate: getFutureDate(10), // Format as yyyy-MM-dd
    refNo: '',
    refDate: getCurrentDate(), // Format as yyyy-MM-dd
    notes: '',
    customerAddress: '',
    totalAmount: 0,
    taxAmount: 0,
    grandTotal: 0,
    isActive: true,
    lineItems: []
  });

  // Load sales order data when editing or viewing
  useEffect(() => {
    
    const loadSalesOrderData = async () => {
      if (params.id && (isEditMode || isViewMode)) {
        try {
          setLoading(true);
          // console.log('Loading sales order with ID:', params.id);
          const response = await salesOrderApiService.getSalesOrderById(params.id);
          // console.log('Sales order API response:', response);
          if (response.success && response.data) {
            const order = response.data;
            // console.log('Sales order data:', order);
            
            // Load line items for this sales order
            let lineItems: LineItem[] = [];
            try {
              const detailsResponse = await salesOrderApiService.getSalesOrderDetails(params.id);
              if (detailsResponse.success && detailsResponse.data) {
                // console.log('Raw API response details:', detailsResponse.data);
                
                // Debug each detail item individually
                detailsResponse.data.forEach((detail, index) => {
                  // console.log('Detail:', detail);
                  console.log(`Detail ${index}:`, {
                    id: detail.id,
                    endgood: detail.endgood,
                    endgoodId: detail.endgood?.id,
                    endgoodCategory: detail.endgoodCategory,
                    endgoodCategoryId: detail.endgoodCategory?.id,
                    partCategory: detail.endgoodCategory?.categoryName,
                    partName: detail.endgood?.endgoodName
                  });
                  
                  // Deep debug the endgood object
                  if (detail.endgood) {
                    console.log(`Detail ${index} endgood object:`, {
                      fullObject: detail.endgood,
                      keys: Object.keys(detail.endgood),
                      id: detail.endgood.id,
                      idType: typeof detail.endgood.id,
                      endgoodName: detail.endgood.endgoodName
                    });
                  }
                  
                  // Deep debug the endgoodCategory object
                  if (detail.endgoodCategory) {
                    console.log(`Detail ${index} endgoodCategory object:`, {
                      fullObject: detail.endgoodCategory,
                      keys: Object.keys(detail.endgoodCategory),
                      id: detail.endgoodCategory.id,
                      idType: typeof detail.endgoodCategory.id,
                      categoryName: detail.endgoodCategory.categoryName
                    });
                  }
                });
                
                lineItems = detailsResponse.data.map(detail => {
                  // Debug the detail object structure
                  console.log('Detail object structure:', {
                    detail,
                    endgood: detail.endgood,
                    endgoodCategory: detail.endgoodCategory,
                    endgoodCategoryName: detail.endgoodCategory?.categoryName,
                    endgoodName: detail.endgood?.endgoodName
                  });
                  
                  // Use the category name from endgood.endgoodCategory.categoryName
                  const partCategory = detail.endgood?.endgoodCategory?.categoryName || '';
                  // Get category ID from endgood.endgoodCategory.id (nested under endgood, not directly on detail)
                  const endgoodCategoryId = detail.endgood?.endgoodCategory?.id?.toString() || 
                                          detail.endgoodCategory?.id?.toString() || '';
                  const hsnCode = detail.endgood?.hsn;
                  const hsnValue = hsnCode?.hsnCode || '';
                  const igstRate = hsnCode?.currentIgstRate || 0;
                  const cgstRate = hsnCode?.currentCgstRate || 0;
                  const sgstRate = hsnCode?.currentSgstRate || 0;
                  
                  // Calculate amounts
                  const baseAmount = detail.unitPrice * detail.quantity;
                  const discountAmount = (baseAmount * (detail.discountPercentage || 0)) / 100;
                  const amountAfterDiscount = baseAmount - discountAmount;
                  const taxAmount = (amountAfterDiscount * (detail.taxPercentage || 0)) / 100;
                  
                  return {
                    id: detail.id.toString(),
                    endgoodId: detail.endgood?.id?.toString() || '',
                    endgoodCategoryId: endgoodCategoryId,
                    partCategory: partCategory,
                    partName: detail.endgood?.endgoodName || '',
                    quantity: detail.quantity,
                    unitPrice: detail.unitPrice,
                    discount: detail.discountPercentage || 0,
                    taxRate: detail.taxPercentage || 0,
                    total: detail.lineTotal || 0,
                    hsn: hsnValue,
                    unit: 'Pieces', // Default unit, can be updated if available from endgood
                    igst: igstRate,
                    cgst: cgstRate,
                    sgst: sgstRate,
                    totalTax: taxAmount,
                    amount: amountAfterDiscount
                  };
                });
                
                // console.log('Mapped line items:', lineItems);
                // console.log('Sample line item structure:', lineItems[0]);
                
                // Debug the mapping process
                // lineItems.forEach((item, index) => {
                //   console.log(`Mapped item ${index}:`, {
                //     id: item.id,
                //     endgoodId: item.endgoodId,
                //     endgoodCategoryId: item.endgoodCategoryId,
                //     partCategory: item.partCategory,
                //     partName: item.partName
                //   });
                // });
              }
            } catch (error) {
              // console.error('Error loading line items:', error);
              // Continue without line items if they fail to load
            }
            
            // console.log('Setting form data for edit mode:', {
            //   orderDate: formatDateForForm(order.orderDate || new Date().toISOString().split('T')[0]),
            //   effectiveStartDate: formatDateForForm(order.effectiveStartDate || ''),
            //   effectiveEndDate: formatDateForForm(order.effectiveEndDate || ''),
            //   refDate: formatDateForForm(order.refDate || ''),
            //   lineItemsCount: lineItems.length,
            //   lineItems: lineItems,
            //   status: order.status
            // });
            
            // console.log('Order customer ID:', order.customerId, 'Type:', typeof order.customerId);
            // console.log('Order object keys:', Object.keys(order));
            // console.log('Order customer object:', (order as any).customer);
            // console.log('Order customerId field:', order.customerId);
            
            // Try to get customer ID from customer object if it exists, otherwise use customerId
            const customerId = (order as any).customer?.id || order.customerId || 0;
            // console.log('Final customer ID to use:', customerId);

            // Load customer address for view/edit (address is not on the SO payload)
            let customerAddress = '';
            if (customerId > 0) {
              try {
                const customerResponse = await customerApiService.getCustomerById(String(customerId));
                if (customerResponse.success && customerResponse.data) {
                  customerAddress = customerResponse.data.address || '';
                }
              } catch {
                // Keep address empty if customer fetch fails
              }
            }
            
            const newFormData = {
              orderNumber: order.soNumber || '',
              orderDate: formatDateForForm(order.orderDate || new Date().toISOString().split('T')[0]),
              status: order.status || 'DRAFT',
              customerId: customerId,
              branchId: order.branchId || 1,
              effectiveStartDate: formatDateForForm(order.effectiveStartDate || ''),
              effectiveEndDate: formatDateForForm(order.effectiveEndDate || ''),
              refNo: order.refNo || '',
              refDate: formatDateForForm(order.refDate || ''),
              notes: order.notes || '',
              customerAddress,
              totalAmount: order.totalAmount || 0,
              taxAmount: order.taxAmount || 0,
              grandTotal: order.grandTotal || 0,
              isActive: order.isActive ?? true,
              lineItems: lineItems
            };
            
            // console.log('Setting form data with dates:', {
            //   orderDate: newFormData.orderDate,
            //   effectiveStartDate: newFormData.effectiveStartDate,
            //   effectiveEndDate: newFormData.effectiveEndDate,
            //   refDate: newFormData.refDate,
            //   customerId: newFormData.customerId,
            //   customersLoaded: customers.length,
            //   availableCustomerIds: customers.map(c => c.id),
            //   orderCustomerId: order.customerId
            // });
            
            setFormData(newFormData);
          }
        } catch (error) {
          // console.error('Error loading sales order:', error);
          toast.error('Failed to load sales order data');
        } finally {
          setLoading(false);
        }
      }
    };

    loadSalesOrderData();
  }, [params.id, isEditMode, isViewMode, categories, customers]);

  // Sync branchId with selectedBranch from header (only in create mode)
  useEffect(() => {
    if (actualMode === 'create' && selectedBranch) {
      setFormData(prev => ({
        ...prev,
        branchId: selectedBranch
      }));
    }
  }, [selectedBranch, actualMode]);

  // Fetch customers on component mount (skip in view mode)
  useEffect(() => {
    if (isViewMode) {
      // console.log('Skipping customer API call in view mode');
      return;
    }

    const loadCustomers = async () => {
      try {
        setCustomersLoading(true);
        const response = await customerApiService.getCustomers(1, 100); // Get up to 100 customers
        if (response.success) {
          setCustomers(response.data);
          // console.log('Loaded customers:', response.data);

          // Debug: Check for customers with invalid IDs or names
          const invalidCustomers = response.data.filter(customer => 
            !customer || 
            !customer.id || 
            customer.id <= 0 || 
            !customer.customerName || 
            customer.customerName.trim() === ''
          );
          if (invalidCustomers.length > 0) {
            // console.warn('Found customers with invalid data:', invalidCustomers);
          }
          
          if (response.data.length === 0) {
            toast.info('No customers found. Please add customers first.');
          }
        }
      } catch (error) {
        // console.error('Error loading customers:', error);
        toast.error('Failed to load customers. Please check your connection and try again.');
        // Keep the existing customers if any, don't clear the array
      } finally {
        setCustomersLoading(false);
      }
    };

    loadCustomers();
  }, [isViewMode]);

  // Fetch categories on component mount (skip in view mode)
  useEffect(() => {
    if (isViewMode) {
      // console.log('Skipping categories API call in view mode');
      return;
    }

    const loadCategories = async () => {
      try {
        setCategoriesLoading(true);
        const categoriesResponse = await endgoodCategoryApiService.getEndgoodCategories();
        setCategories(categoriesResponse);
        // console.log('Loaded categories:', categoriesResponse);
      } catch (error) {
        // console.error('Error loading categories:', error);
        toast.error('Failed to load categories. Please check your connection and try again.');
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, [isViewMode]);

  // Preload endgoods for categories in existing line items (edit mode)
  useEffect(() => {
    if (isEditMode && formData.lineItems.length > 0) {
      const categoryIds = new Set<string>();
      formData.lineItems.forEach(item => {
        if (item.endgoodCategoryId && !endgoodsByCategory[item.endgoodCategoryId]) {
          categoryIds.add(item.endgoodCategoryId);
        }
      });
      
      // Fetch endgoods for each unique category
      categoryIds.forEach(categoryId => {
        fetchEndgoodsByCategory(categoryId);
      });
    }
  }, [isEditMode, formData.lineItems.length]); // Only run when line items are loaded

  // Add a default line item for testing
  useEffect(() => {
    if (actualMode === 'create' && formData.lineItems.length === 0) {
      // Don't add default items when using real inventory data
      // Users should select from available categories and items
    }
  }, [actualMode, formData.lineItems.length]);

  // Debug customer selection
  useEffect(() => {
    if (isEditMode || isViewMode) {
      console.log('Customer selection debug:', {
        customerId: formData.customerId,
        customerIdType: typeof formData.customerId,
        selectValue: formData.customerId > 0 ? formData.customerId.toString() : "",
        customersLength: customers.length,
        customersLoading,
        actualMode,
        availableCustomers: customers.map(c => ({ id: c.id, name: c.customerName })),
        matchingCustomer: customers.find(c => c.id === formData.customerId)
      });
    }
  }, [formData.customerId, customers.length, customersLoading, isEditMode, isViewMode, actualMode, customers]);



  // Static data (in real app, this would come from the database)
  const [statusOptions] = useState([
    'OPEN', 'DRAFT', 'Pending Approval', 'Approved', 'In Production', 'Ready to Ship', 'Shipped', 'Delivered', 'Cancelled'
  ]);


  // Order number will be generated by server
  // const generateOrderNumber = () => {
  //   const prefix = 'SO';
  //   const timestamp = Date.now().toString().slice(-6);
  //   return `${prefix}${timestamp}`;
  // };


  const handleInputChange = (field: keyof SalesOrderData, value: string | number) => {
    // Format dates immediately when they are entered
    let formattedValue = value;
    if (typeof value === 'string' && field.includes('Date')) {
      // If it's a date field, ensure it's in yyyy-MM-dd format
      if (value.includes('T')) {
        formattedValue = value.split('T')[0];
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
  };

  const handleCustomerSelect = (customerId: number) => {
    const selectedCustomer = customers.find(customer => customer.id === customerId);
    if (selectedCustomer) {
      setFormData(prev => ({
        ...prev,
        customerId: customerId,
        customerAddress: selectedCustomer.address || ''
      }));
    }
  };

  // Load all endgoods when dialog opens
  const loadAllEndgoods = async () => {
    try {
      setLoadingAllEndgoods(true);
      const response = await endgoodApiService.getEndgoodsIdNameCategory();
      if (response.success && response.data) {
        // Convert EndgoodIdNameCategoryDto to Endgood format for compatibility
        const endgoods: Endgood[] = response.data.map(dto => ({
          id: dto.id,
          endgoodName: dto.endgoodName,
          partNumber: '',
          sku: '',
          description: '',
          drawingNumber: '',
          drawingImagePath: '',
          productionWeight: 0,
          isActive: true,
          createdBy: 0,
          updatedBy: 0,
          createdAt: '',
          updatedAt: '',
          hsn: {} as any,
          endgoodCategory: {
            id: dto.categoryId,
            categoryName: dto.categoryName,
          } as EndgoodCategory,
        }));
        setAllEndgoods(endgoods);
      }
    } catch (error) {
      // console.error('Error loading endgoods:', error);
      toast.error('Failed to load endgoods');
    } finally {
      setLoadingAllEndgoods(false);
    }
  };

  // Open dialog and load endgoods
  const addLineItem = async () => {
    setEndgoodSearchTerm('');
    
    // Pre-select endgoods that are already in line items
    const existingEndgoodIds = new Set<number>();
    formData.lineItems.forEach(item => {
      if (item.endgoodId) {
        const endgoodId = parseInt(item.endgoodId);
        if (!isNaN(endgoodId)) {
          existingEndgoodIds.add(endgoodId);
        }
      }
    });
    setSelectedEndgoodIds(existingEndgoodIds);
    
    setEndgoodDialogOpen(true);
    await loadAllEndgoods();
  };

  // Group endgoods by category
  const allEndgoodsByCategory = useMemo(() => {
    const grouped: Record<string, Endgood[]> = {};
    allEndgoods.forEach(endgood => {
      const categoryName = endgood.endgoodCategory?.categoryName || 'Uncategorized';
      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(endgood);
    });
    return grouped;
  }, [allEndgoods]);

  // Filter endgoods by search term
  const filteredEndgoodsByCategory = useMemo(() => {
    if (!endgoodSearchTerm.trim()) {
      return allEndgoodsByCategory;
    }
    
    const searchLower = endgoodSearchTerm.toLowerCase().trim();
    const filtered: Record<string, Endgood[]> = {};
    
    Object.keys(allEndgoodsByCategory).forEach(categoryName => {
      const filteredEndgoods = allEndgoodsByCategory[categoryName].filter(endgood =>
        endgood.endgoodName?.toLowerCase().includes(searchLower) ||
        endgood.partNumber?.toLowerCase().includes(searchLower) ||
        endgood.sku?.toLowerCase().includes(searchLower)
      );
      if (filteredEndgoods.length > 0) {
        filtered[categoryName] = filteredEndgoods;
      }
    });
    
    return filtered;
  }, [allEndgoodsByCategory, endgoodSearchTerm]);

  // Handle endgood selection
  const handleEndgoodToggle = (endgoodId: number) => {
    setSelectedEndgoodIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(endgoodId)) {
        newSet.delete(endgoodId);
      } else {
        newSet.add(endgoodId);
      }
      return newSet;
    });
  };

  // Handle select all in category
  const handleSelectAllInCategory = (categoryName: string, checked: boolean) => {
    const categoryEndgoods = filteredEndgoodsByCategory[categoryName] || [];
    setSelectedEndgoodIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        categoryEndgoods.forEach(endgood => newSet.add(endgood.id));
      } else {
        categoryEndgoods.forEach(endgood => newSet.delete(endgood.id));
      }
      return newSet;
    });
  };

  // Check if all endgoods in category are selected
  const isCategoryAllSelected = (categoryName: string) => {
    const categoryEndgoods = filteredEndgoodsByCategory[categoryName] || [];
    return categoryEndgoods.length > 0 && categoryEndgoods.every(endgood => selectedEndgoodIds.has(endgood.id));
  };

  // Add selected endgoods as line items
  const handleAddSelectedEndgoods = () => {
    if (selectedEndgoodIds.size === 0) {
      toast.error('Please select at least one endgood');
      return;
    }

    const newLineItems: LineItem[] = [];
    const existingEndgoodIds = new Set(
      formData.lineItems
        .map(item => item.endgoodId)
        .filter((id): id is string => Boolean(id))
    );
    
    Array.from(selectedEndgoodIds).forEach(endgoodId => {
      if (existingEndgoodIds.has(endgoodId.toString())) {
        return;
      }
      const endgood = allEndgoods.find(e => e.id === endgoodId);
      if (!endgood) return;

      const hsnCode = endgood.hsn;
      const igstRate = hsnCode?.currentIgstRate || 0;
      const cgstRate = hsnCode?.currentCgstRate || 0;
      const sgstRate = hsnCode?.currentSgstRate || 0;
      const taxRate = igstRate > 0 ? igstRate : (cgstRate + sgstRate) || 18;

      const lineItem: LineItem = {
        id: `line_${Date.now()}_${endgoodId}`, // Use 'line_' prefix for new items
        endgoodId: endgood.id.toString(),
        endgoodCategoryId: endgood.endgoodCategory?.id?.toString() || '',
        partCategory: endgood.endgoodCategory?.categoryName || '',
        partName: endgood.endgoodName || '',
        quantity: 0,
        unitPrice: 0,
        discount: 0,
        taxRate: taxRate,
        total: 0,
        hsn: hsnCode?.hsnCode || '',
        unit: 'Pieces',
        igst: igstRate,
        cgst: cgstRate,
        sgst: sgstRate,
        totalTax: 0,
        amount: 0
      };
      newLineItems.push(lineItem);
    });

    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, ...newLineItems]
    }));

    setEndgoodDialogOpen(false);
    setSelectedEndgoodIds(new Set());
    setEndgoodSearchTerm('');
    if (newLineItems.length > 0) {
      toast.success(`Added ${newLineItems.length} item(s) to the order`);
    } else {
      toast.info('Selected items already exist in the order');
    }
  };

  // Parse a single CSV line handling double-quoted fields (e.g. "Name, with comma")
  const parseCsvLine = (line: string): string[] => {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQuotes) {
        if (c === '"') {
          if (line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          current += c;
        }
      } else {
        if (c === '"') {
          inQuotes = true;
        } else if (c === ',' || c === ';' || c === '\t') {
          fields.push(current.trim());
          current = '';
        } else {
          current += c;
        }
      }
    }
    fields.push(current.trim());
    return fields;
  };

  // CSV import: parse file (skip row 1), expect columns name and quantity
  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) {
        toast.error('CSV must have a header row and at least one data row');
        return;
      }
      const rows: { name: string; quantity: number }[] = [];
      for (let i = 1; i < lines.length; i++) {
        const parts = parseCsvLine(lines[i]);
        const name = (parts[0] ?? '').replace(/^["']|["']$/g, '').trim();
        const qty = parseFloat(parts[1] ?? '0') || 0;
        if (name) rows.push({ name, quantity: qty });
      }
      if (rows.length === 0) {
        toast.error('No valid rows found (need name and quantity)');
        return;
      }
      setCsvParsedRows(rows);
      setCsvImportDialogOpen(true);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleApplyCsvImport = async () => {
    if (csvParsedRows.length === 0) return;
    try {
      setLoadingAllEndgoods(true);
      const response = await endgoodApiService.getEndgoodsIdNameCategory();
      const endgoodsList: Endgood[] = (response.success && response.data
        ? (response.data as EndgoodIdNameCategoryDto[]).map((dto) => ({
            id: dto.id,
            endgoodName: dto.endgoodName,
            endgoodCategory: { id: dto.categoryId, categoryName: dto.categoryName } as EndgoodCategory
          } as Endgood))
        : []) as Endgood[];
      const existingEndgoodIds = new Set(formData.lineItems.map((item) => item.endgoodId).filter(Boolean));
      const newLineItems: LineItem[] = [];
      const notFound: string[] = [];
      csvParsedRows.forEach((row) => {
        const nameLower = row.name.trim().toLowerCase();
        const endgood = endgoodsList.find((e) => (e.endgoodName ?? '').trim().toLowerCase() === nameLower);
        if (endgood && !existingEndgoodIds.has(endgood.id.toString())) {
          existingEndgoodIds.add(endgood.id.toString());
          const hsnCode = (endgood as any).hsn;
          const igstRate = hsnCode?.currentIgstRate || 0;
          const cgstRate = hsnCode?.currentCgstRate || 0;
          const sgstRate = hsnCode?.currentSgstRate || 0;
          const taxRate = igstRate > 0 ? igstRate : (cgstRate + sgstRate) || 18;
          newLineItems.push({
            id: `line_${Date.now()}_${endgood.id}_${Math.random().toString(36).slice(2)}`,
            endgoodId: endgood.id.toString(),
            endgoodCategoryId: endgood.endgoodCategory?.id?.toString() || '',
            partCategory: endgood.endgoodCategory?.categoryName || '',
            partName: endgood.endgoodName || '',
            quantity: row.quantity,
            unitPrice: 0,
            discount: 0,
            taxRate,
            total: 0,
            hsn: hsnCode?.hsnCode || '',
            unit: 'Pieces',
            igst: igstRate,
            cgst: cgstRate,
            sgst: sgstRate,
            totalTax: 0,
            amount: 0
          });
        } else if (!endgood) {
          notFound.push(row.name);
        }
      });
      if (notFound.length > 0) {
        toast.warning(`Skipped ${notFound.length} row(s) - no matching endgood: ${notFound.slice(0, 3).join(', ')}${notFound.length > 3 ? '...' : ''}`);
      }
      if (newLineItems.length > 0) {
        setFormData((prev) => ({ ...prev, lineItems: [...prev.lineItems, ...newLineItems] }));
        toast.success(`Added ${newLineItems.length} line item(s) from CSV`);
        setTimeout(() => calculateTotals(), 0);
      }
      setCsvImportDialogOpen(false);
      setCsvParsedRows([]);
    } catch {
      toast.error('Failed to load endgoods for import');
    } finally {
      setLoadingAllEndgoods(false);
    }
  };

  const removeLineItem = async (id: string) => {
    const isExistingItem = !id.startsWith('line_') && !isNaN(parseInt(id));
    if (isExistingItem) {
      try {
        await salesOrderApiService.deleteSalesOrderDetail(id);
      } catch (error) {
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter(item => item.id !== id)
    }));
    calculateTotals();
  };

  const selectItemForLine = (itemId: string, endgoodId: string) => {
    // Find the selected part from the cached category data or all items
    const lineItem = formData.lineItems.find(item => item.id === itemId);
    const categoryId = lineItem?.endgoodCategoryId;
    const categoryEndgoods = categoryId ? endgoodsByCategory[categoryId] : [];
    const selectedPart = categoryEndgoods.find(part => part.id?.toString() === endgoodId) || 
                        items.find(part => part.id?.toString() === endgoodId);
    
    if (selectedPart) {
      const hsnCode = selectedPart.hsn;
      const igstRate = hsnCode?.currentIgstRate || 0;
      const cgstRate = hsnCode?.currentCgstRate || 0;
      const sgstRate = hsnCode?.currentSgstRate || 0;
      
      updateLineItem(itemId, 'endgoodId', endgoodId);
      updateLineItem(itemId, 'partName', selectedPart.endgoodName || '');
      updateLineItem(itemId, 'hsn', hsnCode?.hsnCode || '');
      updateLineItem(itemId, 'unit', 'Pieces'); // Default unit
      updateLineItem(itemId, 'igst', igstRate);
      updateLineItem(itemId, 'cgst', cgstRate);
      updateLineItem(itemId, 'sgst', sgstRate);
      
      // Use the tax rate from HSN code if available
      if (igstRate > 0) {
        updateLineItem(itemId, 'taxRate', igstRate);
      } else if (cgstRate > 0) {
        updateLineItem(itemId, 'taxRate', cgstRate + sgstRate);
      }
    }
  };

  const handleCategoryChange = async (itemId: string, categoryId: string) => {
    const selectedCategory = categories.find(cat => cat.id?.toString() === categoryId);
    if (selectedCategory) {
      // Fetch endgoods for this category if not already loaded
      await fetchEndgoodsByCategory(categoryId);
      
      setFormData(prev => ({
        ...prev,
        lineItems: prev.lineItems.map(item => {
          if (item.id === itemId) {
            const updatedItem = { ...item };
            updatedItem.endgoodCategoryId = categoryId;
            updatedItem.partCategory = selectedCategory.categoryName || '';
            // Clear dependent fields - ensure part name is deselected
            updatedItem.endgoodId = '';
            updatedItem.partName = '';
            updatedItem.hsn = '';
            updatedItem.igst = 0;
            updatedItem.cgst = 0;
            updatedItem.sgst = 0;
            updatedItem.taxRate = 18; // Reset to default tax rate
            updatedItem.unitPrice = 0; // Reset unit price
            // Recalculate totals
            const unitPrice = 0;
            const quantity = updatedItem.quantity || 0;
            const baseAmount = unitPrice * quantity;
            const discountAmount = (baseAmount * (updatedItem.discount || 0)) / 100;
            const amountAfterDiscount = baseAmount - discountAmount;
            const taxAmount = (amountAfterDiscount * (updatedItem.taxRate || 0)) / 100;
            updatedItem.amount = amountAfterDiscount;
            updatedItem.totalTax = taxAmount;
            updatedItem.total = amountAfterDiscount + taxAmount;
            return updatedItem;
          }
          return item;
        })
      }));
      
      // Recalculate totals after updating
      setTimeout(() => {
        calculateTotals();
      }, 0);
    }
  };

  const updateLineItem = (id: string, field: keyof LineItem, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          
          // Auto-populate part details when part is selected
          if (field === 'endgoodId' && value) {
            // Find the selected part from the cached category data or all items
            const categoryId = updatedItem.endgoodCategoryId;
            const categoryEndgoods = categoryId ? endgoodsByCategory[categoryId] : [];
            const selectedPart = categoryEndgoods.find(part => part.id?.toString() === value.toString()) || 
                                items.find(part => part.id?.toString() === value.toString());
            
            if (selectedPart) {
              updatedItem.partName = selectedPart.endgoodName || '';
              const hsnCode = selectedPart.hsn;
              updatedItem.hsn = hsnCode?.hsnCode || '';
              updatedItem.igst = hsnCode?.currentIgstRate || 0;
              updatedItem.cgst = hsnCode?.currentCgstRate || 0;
              updatedItem.sgst = hsnCode?.currentSgstRate || 0;
              updatedItem.unit = 'Pieces'; // Default unit
              
              // Use the tax rate from HSN code if available
              if (hsnCode?.currentIgstRate && hsnCode.currentIgstRate > 0) {
                updatedItem.taxRate = hsnCode.currentIgstRate;
              } else if (hsnCode?.currentCgstRate && hsnCode.currentCgstRate > 0) {
                updatedItem.taxRate = (hsnCode.currentCgstRate + (hsnCode.currentSgstRate || 0));
              }
            }
          }
          
          // Calculate line total and tax
          const unitPrice = updatedItem.unitPrice || 0;
          const quantity = updatedItem.quantity || 0;
          const baseAmount = unitPrice * quantity;
          const discountAmount = (baseAmount * (updatedItem.discount || 0)) / 100;
          const amountAfterDiscount = baseAmount - discountAmount;
          const taxAmount = (amountAfterDiscount * (updatedItem.taxRate || 0)) / 100;
          
          updatedItem.amount = amountAfterDiscount;
          updatedItem.totalTax = taxAmount;
          updatedItem.total = amountAfterDiscount + taxAmount;
          
          return updatedItem;
        }
        return item;
      })
    }));
    
    // Recalculate totals after updating line item
    setTimeout(() => {
      calculateTotals();
    }, 0);
  };

  const saveNewLineItem = async (itemId: string) => {
    const lineItem = formData.lineItems.find(item => item.id === itemId);
    if (!lineItem || !isEditMode || !lineItem.id.startsWith('line_')) {
      return; // Not a new item or not in edit mode
    }

    // Validate required fields
    if (!lineItem.endgoodId || !lineItem.endgoodCategoryId) {
      toast.error('Please select category and part for this item');
      return;
    }

    if (!lineItem.quantity || lineItem.quantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    if (!params.id) {
      toast.error('Sales order ID is missing');
      return;
    }

    try {
      setSavingNewItem(prev => ({ ...prev, [itemId]: true }));
      
      const salesOrderId = parseInt(params.id);
      const baseAmount = (lineItem.unitPrice || 0) * lineItem.quantity;
      const discountAmount = (baseAmount * (lineItem.discount || 0)) / 100;
      const amountAfterDiscount = baseAmount - discountAmount;
      const taxAmount = (amountAfterDiscount * (lineItem.taxRate || 0)) / 100;
      
      const detailData: any = {
        quantity: lineItem.quantity,
        unitPrice: lineItem.unitPrice || 0,
        discountPercentage: lineItem.discount || 0,
        discountAmount: discountAmount,
        taxPercentage: lineItem.taxRate || 0,
        taxAmount: taxAmount,
        lineTotal: amountAfterDiscount + taxAmount,
        salesOrder: salesOrderId.toString(), // API expects string
        salesOrderId: salesOrderId,
        endgoodId: parseInt(lineItem.endgoodId),
        endgoodCategoryId: parseInt(lineItem.endgoodCategoryId),
        createdBy: user?.id || 1,
        updatedBy: user?.id || 1
      };

      const response = await salesOrderApiService.createSalesOrderDetail(detailData);
      
      if (response.success && response.data) {
        const savedDetail = response.data;
        // Update the item ID and other fields from the saved response
        setFormData(prev => ({
          ...prev,
          lineItems: prev.lineItems.map(item => 
            item.id === itemId 
              ? {
                  ...item,
                  id: savedDetail.id.toString(), // Update to the saved ID
                  endgoodId: savedDetail.endgood?.id?.toString() || item.endgoodId,
                  endgoodCategoryId: savedDetail.endgoodCategory?.id?.toString() || item.endgoodCategoryId,
                  quantity: savedDetail.quantity,
                  unitPrice: savedDetail.unitPrice,
                  discount: savedDetail.discountPercentage || 0,
                  taxRate: savedDetail.taxPercentage || 0,
                  total: savedDetail.lineTotal || 0,
                  amount: (savedDetail.lineTotal || 0) - (savedDetail.taxAmount || 0),
                  totalTax: savedDetail.taxAmount || 0,
                  partCategory: savedDetail.endgoodCategory?.categoryName || item.partCategory,
                  partName: savedDetail.endgood?.endgoodName || item.partName
                }
              : item
          )
        }));
        
        // Recalculate totals after saving
        setTimeout(() => {
          calculateTotals();
        }, 0);
        
        toast.success('Line item saved successfully');
      } else {
        toast.error('Failed to save line item');
      }
    } catch (error) {
      // console.error('Error saving new line item:', error);
      toast.error('Failed to save line item');
    } finally {
      setSavingNewItem(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const saveQuantityUpdate = async (itemId: string, quantity: number) => {
    // console.log('saveQuantityUpdate called with itemId:', itemId, 'quantity:', quantity);
    // Only save for existing items in edit mode
    const lineItem = formData.lineItems.find(item => item.id === itemId);
    // console.log('Found lineItem:', lineItem);
    if (!lineItem || !isEditMode || lineItem.id.startsWith('line_') || lineItem.id.startsWith('temp_')) {
      // console.log('Skipping save - not an existing item or not in edit mode');
      return; // Not an existing item, skip auto-save
    }

    if (!lineItem.endgoodId) {
      // console.log('Skipping save - missing required fields', { endgoodId: lineItem.endgoodId });
      return; // Missing required fields
    }

    try {
      // console.log('Starting save process...');
      setSavingQuantity(prev => ({ ...prev, [itemId]: true }));
      
      // Calculate amounts with the updated quantity
      const unitPrice = lineItem.unitPrice || 0;
      const discountPercentage = lineItem.discount || 0;
      const taxPercentage = lineItem.taxRate || 0;
      
      const baseAmount = unitPrice * quantity;
      const discountAmount = (baseAmount * discountPercentage) / 100;
      const amountAfterDiscount = baseAmount - discountAmount;
      const taxAmount = (amountAfterDiscount * taxPercentage) / 100;
      const lineTotal = amountAfterDiscount + taxAmount;
      
      // Send all existing details with updated quantity
      const detailData: any = {
        id: parseInt(lineItem.id),
        quantity: quantity, // Updated quantity from user
        unitPrice: unitPrice, // Existing unit price
        discountPercentage: discountPercentage, // Existing discount
        discountAmount: discountAmount, // Recalculated with new quantity
        taxPercentage: taxPercentage, // Existing tax rate
        taxAmount: taxAmount, // Recalculated with new quantity
        lineTotal: lineTotal, // Recalculated with new quantity
        createdBy: user?.id || 1,
        updatedBy: user?.id || 1,
        salesOrderId: parseInt(params.id || '0'),
        endgoodId: parseInt(lineItem.endgoodId) // Existing endgood ID
      };

      // console.log('Updating sales order detail with ID:', lineItem.id, 'Data:', detailData);
      // console.log('Calling updateSalesOrderDetail API with endpoint:', `/sales/orders/details/${lineItem.id}`);
      const response = await salesOrderApiService.updateSalesOrderDetail(lineItem.id, detailData);
      // console.log('API Response received:', response);
      
      if (response.success) {
        // Update the local state with the saved quantity and recalculated totals
        updateLineItem(itemId, 'quantity', quantity);
        toast.success('Quantity updated successfully');
      } else {
        // console.error('Update failed:', response.message);
        toast.error('Failed to update quantity');
      }
    } catch (error) {
      // console.error('Error saving quantity update:', error);
      toast.error('Failed to update quantity');
    } finally {
      setSavingQuantity(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const fetchEndgoodsByCategory = async (categoryId: string) => {
    if (!categoryId || endgoodsByCategory[categoryId] || loadingEndgoodsByCategory[categoryId]) {
      return; // Already loaded or loading
    }

    try {
      setLoadingEndgoodsByCategory(prev => ({ ...prev, [categoryId]: true }));
      const response = await endgoodApiService.getEndgoodsByCategory(categoryId);
      if (response.success && response.data) {
        setEndgoodsByCategory(prev => ({ ...prev, [categoryId]: response.data }));
      }
    } catch (error) {
      // console.error('Error fetching endgoods by category:', error);
    } finally {
      setLoadingEndgoodsByCategory(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const getPartsForCategory = (categoryId: string) => {
    if (!categoryId || categoryId.toString().trim() === '') {
      return [];
    }
    // Use cached data from API
    return endgoodsByCategory[categoryId] || [];
  };

  const getFilteredEndgoods = (selectedCategoryId: string, currentItemId?: string) => {
    if (!selectedCategoryId) return [];
    
    // Get all currently selected endgood IDs (excluding the current item being edited)
    const selectedEndgoodIds = formData.lineItems
      .filter(item => item.endgoodId && item.id !== currentItemId)
      .map(item => item.endgoodId);
    
    return getPartsForCategory(selectedCategoryId).filter(item => 
      !selectedEndgoodIds.includes(item.id?.toString() || '')
    );
  };

  const getCategoryNameById = (categoryId?: string) => {
    if (!categoryId) return '';
    const cat = categories.find(c => c.id?.toString() === categoryId);
    return cat?.categoryName || '';
  };

  const getPartNameById = (partId?: string) => {
    if (!partId) return '';
    const part = items.find(p => p.id?.toString() === partId);
    return part?.endgoodName || '';
  };

  const calculateTotals = () => {
    setFormData(prev => {
      const subtotal = prev.lineItems.reduce((sum, item) => {
        const unitPrice = item.unitPrice || 0; // Handle undefined/null unit price
        return sum + (item.quantity * unitPrice);
      }, 0);
      
      const totalDiscount = prev.lineItems.reduce((sum, item) => {
        const unitPrice = item.unitPrice || 0; // Handle undefined/null unit price
        return sum + ((item.quantity * unitPrice * item.discount) / 100);
      }, 0);
      
      const totalTax = prev.lineItems.reduce((sum, item) => {
        const unitPrice = item.unitPrice || 0; // Handle undefined/null unit price
        const itemSubtotal = item.quantity * unitPrice;
        const discountAmount = (itemSubtotal * item.discount) / 100;
        const taxableAmount = itemSubtotal - discountAmount;
        return sum + ((taxableAmount * item.taxRate) / 100);
      }, 0);
      
      const grandTotal = subtotal - totalDiscount + totalTax;
      
      return {
        ...prev,
        totalAmount: subtotal,
        taxAmount: totalTax,
        grandTotal
      };
    });
  };

  const getStatusColor = (status: string) => {
    switch (String(status || '').toUpperCase()) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'OPEN': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'CLOSED': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'PENDING APPROVAL': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'APPROVED': return 'bg-green-100 text-green-800 border-green-200';
      case 'IN PRODUCTION': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'READY TO SHIP': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'SHIPPED': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'DELIVERED': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'CANCELLED': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const isOrderClosed = String(formData.status || '').toUpperCase() === 'CLOSED';

  const handleCloseSalesOrder = async () => {
    if (!params.id || !isEditMode) return;
    if (isOrderClosed) {
      toast.info('Sales order is already closed');
      return;
    }

    setClosingOrder(true);
    try {
      const response = await salesOrderApiService.closeSalesOrder(params.id, user?.id);
      if (response.success) {
        setCloseDialogOpen(false);
        setFormData((prev) => ({ ...prev, status: 'CLOSED' }));
        navigate('/sales-orders');
      }
    } catch {
      // API already toasts
    } finally {
      setClosingOrder(false);
    }
  };

  const getPageTitle = () => {
    switch (actualMode) {
      case 'create': return 'New Sales Order';
      case 'edit': return 'Edit Sales Order';
      case 'view': return 'View Sales Order';
      default: return 'Sales Order';
    }
  };

  const getPageIcon = () => {
    switch (actualMode) {
      case 'create': return <Plus className="w-6 h-6" />;
      case 'edit': return <Edit className="w-6 h-6" />;
      case 'view': return <Eye className="w-6 h-6" />;
      default: return <FileText className="w-6 h-6" />;
    }
  };

  const handleCancel = () => {
    navigate('/sales-orders');
  };

  const handleReset = () => {
    setFormData({
      orderNumber: '', // Will be generated by server
      orderDate: getCurrentDate(), // Format as yyyy-MM-dd
      status: 'OPEN', // Always OPEN for new orders
      effectiveStartDate: getCurrentDate(), // Format as yyyy-MM-dd
      effectiveEndDate: getFutureDate(10), // Format as yyyy-MM-dd
      refNo: '',
      refDate: getCurrentDate(), // Format as yyyy-MM-dd
      customerId: 0, // No customer selected by default
      branchId: 1,
      notes: '',
      customerAddress: '',
      totalAmount: 0,
      taxAmount: 0,
      grandTotal: 0,
      isActive: true,
      lineItems: []
    });
    toast.info('Form cleared');
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Basic validation
    if (!formData.customerId) {
      toast.error('Please select a customer');
      return;
    }

    // Check if we have the necessary inventory data for edit mode
    if ((isEditMode || isViewMode) && categories.length === 0) {
      toast.error('Categories are still loading. Please wait and try again.');
      return;
    }

    if (!formData.orderDate) {
      toast.error('Please select an order date');
      return;
    }

    if (formData.lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    // Validate line items
    const invalidItems = formData.lineItems.filter(item => 
      !item.partCategory || !item.partName || item.quantity <= 0
    );
    
    if (invalidItems.length > 0) {
      toast.error('Please fill in all required fields for line items (category, part name, and quantity)');
      return;
    }

    // Validate that all NEW line items have the required IDs
    // For existing items in edit mode (numeric IDs), they should have endgoodId from API
    // Only validate new items (IDs starting with 'line_')
    const newItemsWithoutIds = formData.lineItems.filter(item => {
      const isNewItem = item.id.startsWith('line_');
      if (!isNewItem) return false; // Skip validation for existing items
      
      const missingEndgoodId = !item.endgoodId || item.endgoodId.trim() === '';
      const missingCategoryId = !item.endgoodCategoryId || item.endgoodCategoryId.trim() === '';
      return missingEndgoodId || missingCategoryId;
    });
    
    if (newItemsWithoutIds.length > 0) {
      // console.error('New line items missing IDs:', newItemsWithoutIds);
      toast.error('Some new line items are missing required information. Please reselect the category and part for all new items.');
      return;
    }
    
    // For existing items, ensure they have at least endgoodId (categoryId might be missing but can be derived)
    const existingItemsWithoutEndgoodId = formData.lineItems.filter(item => {
      const isExistingItem = !item.id.startsWith('line_');
      const missingEndgoodId = !item.endgoodId || item.endgoodId.trim() === '';
      return isExistingItem && missingEndgoodId;
    });
    
    if (existingItemsWithoutEndgoodId.length > 0) {
      // console.error('Existing line items missing endgoodId:', existingItemsWithoutEndgoodId);
      toast.error('Some existing line items are missing required information. Please refresh the page and try again.');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare the data for API call - must match SalesOrderData interface exactly
      const apiData = {
        // Include soNumber for edit mode, let server generate for create mode
        ...(actualMode === 'edit' && formData.orderNumber && { soNumber: formData.orderNumber }),
        orderDate: formatDateForAPI(formData.orderDate), // Format as yyyy-MM-dd
        status: actualMode === 'create' ? 'OPEN' : formData.status, // Always OPEN for new orders
        effectiveStartDate: formatDateForAPI(formData.effectiveStartDate || formData.orderDate), // Format as yyyy-MM-dd
        effectiveEndDate: formatDateForAPI(formData.effectiveEndDate || formData.orderDate), // Format as yyyy-MM-dd
        // expectedDeliveryDate: formatDateForAPI(formData.expectedDeliveryDate || formData.orderDate), // Format as yyyy-MM-dd
        notes: formData.notes || '',
        refNo: formData.refNo || '', // Use refNo from new interface
        refDate: formatDateForAPI(formData.refDate || formData.orderDate), // Format as yyyy-MM-dd
        totalAmount: formData.totalAmount || 0,
        taxAmount: formData.taxAmount || 0,
        grandTotal: formData.grandTotal || 0,
        isActive: true // Required by API interface
      };

      // console.log('Form data:', formData);
      // console.log('API data being sent:', apiData);
      console.log('SO Number check:', {
        actualMode,
        formDataOrderNumber: formData.orderNumber,
        apiDataSoNumber: apiData.soNumber,
        hasSoNumber: 'soNumber' in apiData
      });
      // console.log('Customer ID:', formData.customerId);
      // console.log('Branch ID:', selectedBranch || 0);
      
      // Debug: Log all date fields specifically
      // console.log('Date debugging:');
      // console.log('- orderDate (form):', formData.orderDate, '-> (API):', apiData.orderDate);
      // console.log('- effectiveStartDate (form):', formData.effectiveStartDate, '-> (API):', apiData.effectiveStartDate);
      // console.log('- effectiveEndDate (form):', formData.effectiveEndDate, '-> (API):', apiData.effectiveEndDate);
      // console.log('- expectedDeliveryDate (form):', formData.expectedDeliveryDate, '-> (API):', apiData.expectedDeliveryDate);
      // console.log('- refDate (form):', formData.refDate, '-> (API):', apiData.refDate);

      // Prepare customer and branch IDs for both create and edit modes
      const customerId = formData.customerId;
      const branchId = selectedBranch || 0;
      
      if (actualMode === 'create') {
        // Create new sales order
        // console.log('Creating sales order with data:', { apiData, customerId, branchId });
        
        const response = await salesOrderApiService.createSalesOrder(apiData, customerId, branchId);
        // console.log('Create response:', response);
        
        if (response.success) {
          // Save line items for the created sales order using batch endpoint
          const salesOrderId = response.data.id;
          // console.log('Sales order created with ID:', salesOrderId);
          
          // Prepare batch data for all line items
          const batchDetails: any[] = [];
          
          for (const lineItem of formData.lineItems) {
            // Use the stored IDs directly from the line item
            if (!lineItem.endgoodId) {
              // console.error('Missing endgoodId for line item:', lineItem);
              continue;
            }
            
            const endgoodId = parseInt(lineItem.endgoodId, 10);
            if (isNaN(endgoodId) || endgoodId === 0) continue;
            
            const detailData: any = {
              quantity: lineItem.quantity,
              unitPrice: lineItem.unitPrice,
              discountPercentage: lineItem.discount,
              discountAmount: (lineItem.quantity * (lineItem.unitPrice || 0) * lineItem.discount) / 100,
              taxPercentage: lineItem.taxRate,
              taxAmount: ((lineItem.quantity * (lineItem.unitPrice || 0)) - ((lineItem.quantity * (lineItem.unitPrice || 0) * lineItem.discount) / 100)) * (lineItem.taxRate / 100),
              lineTotal: lineItem.total,
              createdBy: user?.id || 1,
              updatedBy: user?.id || 1,
              salesOrder: salesOrderId.toString(),
              salesOrderId: salesOrderId,
              endgoodId,
            };
            if (lineItem.endgoodCategoryId) {
              const endgoodCategoryId = parseInt(lineItem.endgoodCategoryId, 10);
              if (!isNaN(endgoodCategoryId) && endgoodCategoryId !== 0) {
                detailData.endgoodCategoryId = endgoodCategoryId;
                detailData.categoryId = endgoodCategoryId;
              }
            }
            batchDetails.push(detailData);
          }

          if (batchDetails.length > 0) {
            // console.log('Sending batch create request for', batchDetails.length, 'line items');
            const batchResponse = await salesOrderApiService.batchCreateSalesOrderDetails(salesOrderId.toString(), batchDetails);
            
            if (batchResponse.success) {
              toast.success(`Sales order and ${batchDetails.length} line items created successfully!`);
            } else {
              toast.warning(`Sales order created but line items failed to save: ${batchResponse.message}`);
            }
          } else {
            toast.success('Sales order created successfully!');
          }
          
          navigate('/sales-orders');
        } else {
          toast.error(`Failed to create sales order: ${response.message}`);
        }
      } else if (actualMode === 'edit' && params.id) {
        // Update existing sales order
        // console.log('Updating sales order with data:', { id: params.id, apiData, customerId, branchId });
        
        const response = await salesOrderApiService.updateSalesOrder(params.id, apiData, customerId, branchId);
        // console.log('Update response:', response);
        
        if (response.success) {
          // For updates, we need to handle line items
          const salesOrderId = parseInt(params.id);
          // console.log('Sales order updated, now handling line items for ID:', salesOrderId);
          
          try {
            // Prepare batch data for all line items (both new and existing)
            const batchDetails: any[] = [];
            
            for (const lineItem of formData.lineItems) {
              // Use the stored IDs directly from the line item
              if (!lineItem.endgoodId) {
                // console.error('Missing endgoodId for line item:', lineItem);
                continue;
              }
              
              const endgoodId = parseInt(lineItem.endgoodId, 10);
              if (isNaN(endgoodId) || endgoodId === 0) continue;
              const detailData: any = {
                quantity: lineItem.quantity,
                unitPrice: lineItem.unitPrice,
                discountPercentage: lineItem.discount,
                discountAmount: (lineItem.quantity * (lineItem.unitPrice || 0) * lineItem.discount) / 100,
                taxPercentage: lineItem.taxRate,
                taxAmount: ((lineItem.quantity * (lineItem.unitPrice || 0)) - ((lineItem.quantity * (lineItem.unitPrice || 0) * lineItem.discount) / 100)) * (lineItem.taxRate / 100),
                lineTotal: lineItem.total,
                createdBy: user?.id || 1,
                updatedBy: user?.id || 1,
                salesOrderId: salesOrderId,
                endgoodId,
              };
              if (lineItem.endgoodCategoryId) {
                const endgoodCategoryId = parseInt(lineItem.endgoodCategoryId, 10);
                if (!isNaN(endgoodCategoryId) && endgoodCategoryId !== 0) {
                  detailData.endgoodCategoryId = endgoodCategoryId;
                  detailData.categoryId = endgoodCategoryId;
                }
              }

              // In edit mode, include id for existing items (items with numeric IDs, not starting with 'line_' or 'temp_')
              if (lineItem.id && !lineItem.id.startsWith('temp_') && !lineItem.id.startsWith('line_') && !isNaN(parseInt(lineItem.id))) {
                // Existing line item - include id for update
                detailData.id = parseInt(lineItem.id);
              }
              // For new items (starting with 'line_'), don't include id field - will be created
              
              batchDetails.push(detailData);
            }

            if (batchDetails.length > 0) {
              // console.log('Sending batch request for', batchDetails.length, 'line items');
              const batchResponse = await salesOrderApiService.batchUpdateSalesOrderDetails(salesOrderId.toString(), batchDetails);
              
              if (batchResponse.success) {
                // console.log(`Successfully updated ${batchDetails.length} line items via batch`);
                toast.success(`Sales order and ${batchDetails.length} line items updated successfully!`);
              } else {
                // console.error('Batch update failed:', batchResponse.message);
                toast.warning(`Sales order updated but line items failed to save: ${batchResponse.message}`);
              }
            } else {
              toast.success('Sales order updated successfully!');
            }
            
          } catch (error) {
            // console.error('Error handling line items during update:', error);
            toast.warning('Sales order updated but there was an error handling line items. Please check the console.');
          }
          
          navigate('/sales-orders');
        } else {
          toast.error(`Failed to update sales order: ${response.message}`);
        }
      }
    } catch (error) {
      // console.error('Error saving sales order:', error);
      toast.error(`Failed to save sales order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Card className="w-full border-0 shadow-lg bg-white/70 backdrop-blur-sm">
      <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="border-primary-300 hover:bg-primary-100 text-primary-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <CardTitle className="text-gray-900">{getPageTitle()}</CardTitle>
              <CardDescription className="text-gray-600">
                {actualMode === 'create' && 'Create a new sales order with customer and product details'}
                {actualMode === 'edit' && 'Update sales order information and details'}
                {actualMode === 'view' && 'View sales order details and information'}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditMode && !isOrderClosed && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCloseDialogOpen(true)}
                disabled={loading || closingOrder}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Ban className="w-4 h-4 mr-2" />
                Close Sales Order
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSave} className="space-y-8">
          {/* Order Information Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
                  {getPageIcon()}
                </div>
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                  Order Information
                </h3>
              </div>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            {/* First Row - Order Number, Order Date, Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* Order Number - Read-only for view and edit modes */}
              {(actualMode === 'view' || actualMode === 'edit') && (
                <div className="space-y-2">
                  <Label htmlFor="orderNumber" className="text-gray-700 flex items-center gap-1">
                    <Hash className="h-4 w-4" />
                    Order Number
                  </Label>
                  <Input
                    id="orderNumber"
                    value={formData.orderNumber || 'N/A'}
                    className="border-primary-200 bg-gray-50 text-gray-700"
                    disabled={true}
                    readOnly
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="orderDate" className="text-gray-700 flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Order Date *
                </Label>
                <Input
                  id="orderDate"
                  type="date"
                  value={formData.orderDate}
                  onChange={(e) => handleInputChange('orderDate', e.target.value)}
                  required
                  max="2099-12-31"
                  className={`border-primary-200 ${isDateRefReadOnly ? 'bg-gray-50 text-gray-700' : 'focus:border-primary-400 focus:ring-primary-400'}`}
                  disabled={isDateRefReadOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status" className="text-gray-700">Status</Label>
                {actualMode === 'create' ? (
                  <div className="flex items-center gap-2">
                    <Input
                      id="status"
                      value="OPEN"
                      className="border-primary-200 bg-gray-50 text-gray-500"
                      disabled={true}
                      readOnly
                    />
                    
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Badge className={`${getStatusColor(formData.status)} border-0 text-sm px-3 py-1`}>
                      {formData.status}
                    </Badge>
                  </div>
                )}
                {actualMode === 'create' && (
                  <p className="text-xs text-gray-500">Status is automatically set to OPEN for new orders</p>
                )}
              </div>
            </div>

            {/* Second Row - Effective Dates, Expected Delivery */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="effectiveStartDate" className="text-gray-700">Effective Start Date</Label>
                <Input
                  id="effectiveStartDate"
                  type="date"
                  value={formData.effectiveStartDate}
                  onChange={(e) => handleInputChange('effectiveStartDate', e.target.value)}
                  max="2099-12-31"
                  className={`border-primary-200 ${isDateRefReadOnly ? 'bg-gray-50 text-gray-700' : 'focus:border-primary-400 focus:ring-primary-400'}`}
                  disabled={isDateRefReadOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="effectiveEndDate" className="text-gray-700">Effective End Date</Label>
                <Input
                  id="effectiveEndDate"
                  type="date"
                  value={formData.effectiveEndDate}
                  onChange={(e) => handleInputChange('effectiveEndDate', e.target.value)}
                  max="2099-12-31"
                  className={`border-primary-200 ${isDateRefReadOnly ? 'bg-gray-50 text-gray-700' : 'focus:border-primary-400 focus:ring-primary-400'}`}
                  disabled={isDateRefReadOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expectedDeliveryDate" className="text-gray-700">Expected Delivery Date</Label>
                <Input
                  id="expectedDeliveryDate"
                  type="date"
                  value={formData.effectiveEndDate}
                  onChange={(e) => handleInputChange('effectiveEndDate', e.target.value)}
                  max="2099-12-31"
                  className={`border-primary-200 ${isDateRefReadOnly ? 'bg-gray-50 text-gray-700' : 'focus:border-primary-400 focus:ring-primary-400'}`}
                  disabled={isDateRefReadOnly}
                />
              </div>
            </div>

            {/* Third Row - Reference Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="referenceNumber" className="text-gray-700">Reference Number</Label>
                <Input
                  id="referenceNumber"
                  value={formData.refNo}
                  onChange={(e) => handleInputChange('refNo', e.target.value)}
                  placeholder="Enter reference number"
                  className={`border-primary-200 ${isDateRefReadOnly ? 'bg-gray-50 text-gray-700' : 'focus:border-primary-400 focus:ring-primary-400'}`}
                  disabled={isDateRefReadOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="referenceDate" className="text-gray-700">Reference Date</Label>
                <Input
                  id="referenceDate"
                  type="date"
                  value={formData.refDate}
                  onChange={(e) => handleInputChange('refDate', e.target.value)}
                  max="2099-12-31"
                  className={`border-primary-200 ${isDateRefReadOnly ? 'bg-gray-50 text-gray-700' : 'focus:border-primary-400 focus:ring-primary-400'}`}
                  disabled={isDateRefReadOnly}
                />
              </div>
            </div>

            {/* Customer Information */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary-600" />
                  <h4 className="font-medium text-gray-900">Customer Information</h4>
                </div>
                {!isHeaderReadOnly && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const loadCustomers = async () => {
                        try {
                          setCustomersLoading(true);
                          const response = await customerApiService.getCustomers(1, 100);
                          if (response.success) {
                            setCustomers(response.data);
                            toast.success('Customer list refreshed');
                          }
                        } catch (error) {
                          // console.error('Error refreshing customers:', error);
                          toast.error('Failed to refresh customers');
                        } finally {
                          setCustomersLoading(false);
                        }
                      };
                      loadCustomers();
                    }}
                    disabled={customersLoading}
                    className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600 text-xs h-8"
                  >
                    {customersLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                    ) : (
                      '↻'
                    )}
                  </Button>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="customerName" className="text-gray-700">Customer Name *</Label>
                {isHeaderReadOnly ? (
                  <Input
                    value={
                      customers.find((c) => c.id === formData.customerId)?.customerName ||
                      (salesOrder as { customerName?: string } | undefined)?.customerName ||
                      'N/A'
                    }
                    className="border-primary-200 bg-gray-50 text-gray-700"
                    disabled={true}
                    readOnly
                  />
                ) : (
                  <Select 
                    value={formData.customerId > 0 ? formData.customerId.toString() : ""} 
                    onValueChange={(value) => handleCustomerSelect(parseInt(value))}
                    disabled={customersLoading}
                  >
                    <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue placeholder={customersLoading ? "Loading customers..." : "Select customer"} />
                    </SelectTrigger>
                    <SelectContentWithSearch searchPlaceholder="Search customers...">
                      {customersLoading ? (
                        <div className="px-2 py-1.5 text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                            Loading customers...
                          </div>
                        </div>
                      ) : customers.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-gray-500 text-center">
                          <div className="text-gray-500 mb-1">No customers found</div>
                          <div className="text-xs text-gray-400">Please add customers first</div>
                        </div>
                      ) : (
                        customers
                          .filter(customer => 
                            customer && 
                            customer.id && 
                            customer.id > 0 && 
                            customer.customerName && 
                            customer.customerName.trim() !== ''
                          )
                          .map((customer) => (
                            <SelectItem key={customer.id} value={customer.id.toString()}>
                              <div className="flex flex-col">
                                <span className="font-medium">{customer.customerName}</span>
                                <span className="text-xs text-gray-500">{customer.mobileNo || 'N/A'}</span>
                              </div>
                            </SelectItem>
                          ))
                      )}
                    </SelectContentWithSearch>
                  </Select>
                )}
                {!isHeaderReadOnly && customers.length === 0 && !customersLoading && (
                  <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded-md border border-amber-200">
                    <div className="flex items-center gap-2">
                      <span>⚠️</span>
                      <span>No customers available. Please add customers in the Customer Management section first.</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerAddress" className="text-gray-700">Customer Address</Label>
                <Textarea
                  id="customerAddress"
                  value={formData.customerAddress}
                  onChange={(e) => handleInputChange('customerAddress', e.target.value)}
                  placeholder="Customer address will be auto-filled when customer is selected"
                  rows={2}
                  className={`border-primary-200 ${isHeaderReadOnly ? 'bg-gray-50 text-gray-700' : 'focus:border-primary-400 focus:ring-primary-400'}`}
                  disabled={isHeaderReadOnly}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-gray-700">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Enter any additional notes or comments"
                rows={3}
                className={`border-primary-200 ${isHeaderReadOnly ? 'bg-gray-50 text-gray-700' : 'focus:border-primary-400 focus:ring-primary-400'}`}
                disabled={isHeaderReadOnly}
              />
            </div>
          </div>

          {/* Line Items Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
                    <Calculator className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                    Line Items
                  </h3>
                  {!isViewMode && (
                    <>
                      <Button
                        type="button"
                        onClick={addLineItem}
                        className="ml-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Item
                      </Button>
                      <input
                        ref={csvFileInputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={handleCsvFileChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => csvFileInputRef.current?.click()}
                        className="ml-2 border-primary-300 text-primary-700 hover:bg-primary-50 shadow"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload CSV
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            {formData.lineItems && formData.lineItems.length > 0 ? (
              <div className="border border-primary-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                        <TableHead className="w-[60px]">S.No</TableHead>
                        <TableHead className="w-[150px]">Part Category</TableHead>
                        <TableHead className="w-[200px]">Endgood Name</TableHead>
                        <TableHead className="w-[100px]">Quantity</TableHead>
                        <TableHead className="w-[120px]">Unit Price (Optional)</TableHead>
                        <TableHead className="w-[100px]">Discount %</TableHead>
                        <TableHead className="w-[100px]">Tax %</TableHead>
                        <TableHead className="w-[120px]">Total</TableHead>
                        <TableHead className="w-[50px]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.lineItems.map((item, itemIndex) => {
                        // Check if this is an existing item (numeric ID, not starting with 'line_')
                        const isExistingItem = isEditMode && !item.id.startsWith('line_') && !isNaN(parseInt(item.id));
                        
                        return (
                          <TableRow key={item.id}>
                            <TableCell>{itemIndex + 1}</TableCell>
                            <TableCell>
                              <div className="font-medium">{item.partCategory || '-'}</div>
                            </TableCell>
                            <TableCell>
                              {isExistingItem || item.partName ? (
                                <div className="font-medium">{item.partName || '-'}</div>
                              ) : (
                                <Select 
                                  value={item.endgoodId || ''}
                                  onValueChange={(value) => selectItemForLine(item.id, value)}
                                  disabled={!item.endgoodCategoryId || loadingEndgoodsByCategory[item.endgoodCategoryId] || isViewMode}
                                >
                                  <SelectTrigger className={`border-primary-200 focus:border-primary-400 flex-1 ${!item.endgoodId ? 'border-red-400 bg-red-50' : ''}`}>
                                    <SelectValue placeholder={
                                      !item.endgoodCategoryId ? "Select category first" : 
                                      itemsLoading ? "Loading..." : 
                                      "Select part"
                                    } />
                                  </SelectTrigger>
                                  <SelectContentWithSearch searchPlaceholder="Search parts...">
                                    {loadingEndgoodsByCategory[item.endgoodCategoryId] ? (
                                      <div className="px-2 py-1.5 text-sm text-gray-500">
                                        <div className="flex items-center gap-2">
                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                                          Loading items...
                                        </div>
                                      </div>
                                    ) : getFilteredEndgoods(item.endgoodCategoryId, item.id).length === 0 ? (
                                      <div className="px-2 py-1.5 text-sm text-gray-500 text-center">
                                        {!item.endgoodCategoryId ? "Select category first" : "All items in this category are already selected"}
                                      </div>
                                    ) : (
                                      getFilteredEndgoods(item.endgoodCategoryId, item.id)
                                        .filter(part => 
                                          part && 
                                          part.id && 
                                          part.endgoodName && 
                                          part.endgoodName.trim() !== ''
                                        )
                                        .map((part, index) => (
                                          <SelectItem key={`${itemIndex}-${part.id}-${index}`} value={part.id?.toString() || ''}>
                                            {part.endgoodName}
                                          </SelectItem>
                                        ))
                                    )}
                                  </SelectContentWithSearch>
                                </Select>
                              )}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="text"
                                value={rawInputs[item.id]?.qty ?? (item.quantity?.toString() || '0')}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setRawInputs(prev => ({ ...prev, [item.id]: { ...prev[item.id], qty: val } }));
                                  const numVal = parseFloat(val);
                                  if (!isNaN(numVal)) {
                                    updateLineItem(item.id, 'quantity', numVal);
                                  }
                                }}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  updateLineItem(item.id, 'quantity', val);
                                  setRawInputs(prev => {
                                    const newState = { ...prev };
                                    delete newState[item.id]?.qty;
                                    return newState;
                                  });
                                }}
                                onFocus={(e) => e.target.select()}
                                className="border-2 border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 w-full font-semibold text-lg bg-blue-50/50 py-2.5"
                                placeholder="0"
                                disabled={isViewMode || savingQuantity[item.id]}
                              />
                            </TableCell>
                            <TableCell>
                              {isExistingItem ? (
                                <Input
                                  value={item.unitPrice}
                                  className="border-primary-200 bg-gray-50 text-gray-700"
                                  disabled={true}
                                  readOnly
                                />
                              ) : (
                                <Input
                                  type="text"
                                  value={rawInputs[item.id]?.rate ?? (item.unitPrice?.toString() || '0')}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setRawInputs(prev => ({ ...prev, [item.id]: { ...prev[item.id], rate: val } }));
                                    const numVal = parseFloat(val);
                                    if (!isNaN(numVal)) {
                                      updateLineItem(item.id, 'unitPrice', numVal);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    updateLineItem(item.id, 'unitPrice', val);
                                    setRawInputs(prev => {
                                      const newState = { ...prev };
                                      delete newState[item.id]?.rate;
                                      return newState;
                                    });
                                  }}
                                  onFocus={(e) => e.target.select()}
                                  className="border-2 border-primary-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 w-full font-semibold text-base bg-primary-50/50 py-2.5"
                                  placeholder="Optional"
                                  disabled={isViewMode}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {isExistingItem ? (
                                <Input
                                  value={item.discount}
                                  className="border-primary-200 bg-gray-50 text-gray-700"
                                  disabled={true}
                                  readOnly
                                />
                              ) : (
                                <Input
                                  value={item.discount}
                                  onChange={(e) => updateLineItem(item.id, 'discount', Number(e.target.value))}
                                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                                  disabled={isViewMode}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {isExistingItem ? (
                                <Input
                                  value={item.taxRate}
                                  className="border-primary-200 bg-gray-50 text-gray-700"
                                  disabled={true}
                                  readOnly
                                />
                              ) : (
                                <Input
                                  value={item.taxRate}
                                  onChange={(e) => updateLineItem(item.id, 'taxRate', Number(e.target.value))}
                                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                                  disabled={isViewMode}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.total.toFixed(2)}
                                readOnly
                                className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-primary-200"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {isEditMode && isExistingItem && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      // console.log('Save button clicked for item:', item.id);
                                      const currentQty = parseFloat((rawInputs[item.id]?.qty ?? item.quantity?.toString()) || '0') || 0;
                                      // console.log('Current quantity:', currentQty, 'Original quantity:', item.quantity);
                                      await saveQuantityUpdate(item.id, currentQty);
                                    }}
                                    disabled={savingQuantity[item.id] || isViewMode}
                                    className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600"
                                    title="Save quantity"
                                  >
                                    {savingQuantity[item.id] ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    ) : (
                                      <Check className="h-4 w-4 text-green-600" />
                                    )}
                                  </Button>
                                )}
                                {isEditMode && !isExistingItem && item.id.startsWith('line_') && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      await saveNewLineItem(item.id);
                                    }}
                                    disabled={savingNewItem[item.id] || isViewMode || !item.endgoodId || !item.endgoodCategoryId || !item.quantity || item.quantity <= 0}
                                    className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600"
                                    title="Save this item"
                                  >
                                    {savingNewItem[item.id] ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    ) : (
                                      <Check className="h-4 w-4 text-green-600" />
                                    )}
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => removeLineItem(item.id)}
                                  className="border-red-200 hover:bg-red-50 text-red-600"
                                  disabled={isViewMode}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-primary-50/30 to-white rounded-lg border border-primary-200">
                <Calculator className="h-12 w-12 mx-auto mb-3 text-primary-300" />
                <p>No line items added yet. Click "Add Item" to get started.</p>
              </div>
            )}
            
            {/* Add Item Button at Bottom */}
            {!isViewMode && (
              <div className="flex justify-start">
                <Button
                  type="button"
                  onClick={addLineItem}
                  className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </div>
            )}
          </div>


          {/* Order Summary Section removed for add/edit and view */}

          {/* Create / Update action button */}
          {(actualMode === 'create' || actualMode === 'edit') && !isViewMode && (
            <div className="flex items-center justify-end pt-6 border-t border-gray-200">
              <Button
                type="submit"
                disabled={loading || (isEditMode && isOrderClosed)}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {actualMode === 'create' ? 'Creating...' : 'Updating...'}
                  </div>
                ) : actualMode === 'create' ? (
                  'Create Order'
                ) : (
                  'Update Order'
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>

    <Dialog
      open={closeDialogOpen}
      onOpenChange={(open) => {
        if (closingOrder) return;
        setCloseDialogOpen(open);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Close sales order?</DialogTitle>
          <DialogDescription>
            This will set status to <strong>CLOSED</strong>
            {formData.orderNumber ? ` for ${formData.orderNumber}` : ''}. Closed orders are no longer active for fulfillment.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCloseDialogOpen(false)}
            disabled={closingOrder}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleCloseSalesOrder()}
            disabled={closingOrder}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {closingOrder ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Closing...
              </>
            ) : (
              <>
                <Ban className="w-4 h-4 mr-2" />
                Close Order
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Endgood Selection Dialog */}
    <Dialog open={endgoodDialogOpen} onOpenChange={setEndgoodDialogOpen}>
      <DialogContent className="!max-w-[98vw] !w-[98vw] max-h-[90vh] overflow-hidden flex flex-col p-6">
        <DialogHeader>
          <DialogTitle>Select Endgoods</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search endgoods by name, part number, or SKU..."
              value={endgoodSearchTerm}
              onChange={(e) => setEndgoodSearchTerm(e.target.value)}
              className="pl-10 pr-10"
            />
            {endgoodSearchTerm && (
              <button
                onClick={() => setEndgoodSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Endgoods List by Category */}
          <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg p-4">
            {loadingAllEndgoods ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading endgoods...</p>
                </div>
              </div>
            ) : Object.keys(filteredEndgoodsByCategory).length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No endgoods found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Object.keys(filteredEndgoodsByCategory).sort().map(categoryName => {
                  const categoryEndgoods = filteredEndgoodsByCategory[categoryName];
                  const isAllSelected = isCategoryAllSelected(categoryName);
                  
                  return (
                    <div key={categoryName} className="space-y-2 border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 pb-2 border-b border-gray-300">
                        <Checkbox
                          checked={isAllSelected}
                          onCheckedChange={(checked) => handleSelectAllInCategory(categoryName, checked as boolean)}
                          className="border-primary-300"
                        />
                        <h4 className="font-semibold text-primary-700 text-sm">{categoryName}</h4>
                        <span className="text-xs text-gray-500">({categoryEndgoods.length})</span>
                      </div>
                      <div className="space-y-1 max-h-[400px] overflow-y-auto">
                        {categoryEndgoods.map(endgood => (
                          <div
                            key={endgood.id}
                            className="flex items-center gap-2 p-2 rounded hover:bg-white cursor-pointer"
                            onClick={() => handleEndgoodToggle(endgood.id)}
                          >
                            <Checkbox
                              checked={selectedEndgoodIds.has(endgood.id)}
                              onCheckedChange={() => handleEndgoodToggle(endgood.id)}
                              className="border-primary-300"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {endgood.endgoodName}
                              </div>
                              {(endgood.partNumber || endgood.sku) && (
                                <div className="text-xs text-gray-500 truncate">
                                  {endgood.partNumber && `Part: ${endgood.partNumber}`}
                                  {endgood.partNumber && endgood.sku && ' • '}
                                  {endgood.sku && `SKU: ${endgood.sku}`}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selection Summary */}
          {selectedEndgoodIds.size > 0 && (
            <div className="text-sm text-gray-600 border-t pt-2">
              {selectedEndgoodIds.size} endgood(s) selected
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setEndgoodDialogOpen(false);
              setSelectedEndgoodIds(new Set());
              setEndgoodSearchTerm('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedEndgoods}
            disabled={selectedEndgoodIds.size === 0}
            className="bg-primary-600 hover:bg-primary-700"
          >
            Add Selected ({selectedEndgoodIds.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* CSV Import Preview Dialog */}
    <Dialog open={csvImportDialogOpen} onOpenChange={setCsvImportDialogOpen}>
      <DialogContent className="!max-w-[95vw] !w-[95vw] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Import line items from CSV</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">First row (header) was skipped. Review and confirm to add these items.</p>
        <div className="overflow-auto border rounded-lg flex-1 min-h-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-primary-50">
                <TableHead className="w-[60px]">#</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-[100px]">Quantity</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {csvParsedRows.map((row, idx) => {
                const nameLower = row.name.trim().toLowerCase();
                const endgood = allEndgoods.find((e) => (e.endgoodName ?? '').trim().toLowerCase() === nameLower);
                const status = loadingAllEndgoods ? '…' : endgood ? `Matched: ${endgood.endgoodName}` : 'Not found';
                return (
                  <TableRow key={idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.quantity}</TableCell>
                    <TableCell className={endgood ? 'text-green-700' : 'text-amber-600'}>{status}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setCsvImportDialogOpen(false); setCsvParsedRows([]); }}>
            Cancel
          </Button>
          <Button onClick={handleApplyCsvImport} disabled={loadingAllEndgoods}>
            {loadingAllEndgoods ? 'Loading…' : 'Confirm & Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}